import { create } from "zustand";
import { createPortal } from "react-dom";
import { useEffect } from "react";

interface Toast {
  id: string;
  text: string;
  tone: "default" | "success" | "error";
}

interface ToastState {
  items: Toast[];
  push: (t: Omit<Toast, "id">) => void;
  remove: (id: string) => void;
}

export const useToasts = create<ToastState>((set) => ({
  items: [],
  push: (t) => {
    const id = `t_${Math.random().toString(36).slice(2, 9)}`;
    set((s) => ({ items: [...s.items, { id, ...t }] }));
    window.setTimeout(() => {
      set((s) => ({ items: s.items.filter((x) => x.id !== id) }));
    }, 3500);
  },
  remove: (id) => set((s) => ({ items: s.items.filter((x) => x.id !== id) })),
}));

export function toast(text: string, tone: Toast["tone"] = "default"): void {
  useToasts.getState().push({ text, tone });
}

export function ToastViewport() {
  const items = useToasts((s) => s.items);
  const remove = useToasts((s) => s.remove);
  useEffect(() => undefined, []);
  const root = document.getElementById("toast-root") ?? document.body;

  return createPortal(
    <div
      className="fixed bottom-4 right-4 z-[110] flex flex-col gap-2"
      role="region"
      aria-label="Notifications"
    >
      {items.map((t) => (
        <div
          key={t.id}
          role="status"
          className={`card px-3 py-2 text-[12px] shadow-e3 ${
            t.tone === "success"
              ? "border-status-running/30"
              : t.tone === "error"
                ? "border-status-error/30"
                : ""
          }`}
        >
          <button
            className="absolute right-2 top-1 text-tertiary hover:text-primary"
            onClick={() => remove(t.id)}
            aria-label="Dismiss"
            type="button"
          >
            ×
          </button>
          <div className="pr-4 text-primary">{t.text}</div>
        </div>
      ))}
    </div>,
    root,
  );
}
